import type { FC } from "react";
import { View } from "@visioni/a3s-ui/components";
import "@visioni/a3s-ui/theme";
import { StyleSheet } from "react-native-unistyles";
import {
  NotificationPreferenceGroup,
  NO_UNAVAILABLE_CHANNELS,
  type NotificationCategory,
  type NotificationCategoryPreferences,
  type NotificationChannel,
} from "../NotificationPreferenceGroup/NotificationPreferenceGroup";
import { NotificationScopeListGroup } from "../NotificationScopeListGroup/NotificationScopeListGroup";
import { NotificationContactPhoneField } from "../NotificationContactPhoneField/NotificationContactPhoneField";

/**
 * SMS has nowhere to deliver to until a phone number is on file, so its
 * checkboxes are disabled until one is — a checked SMS box with no number
 * behind it promises a message that never arrives. This tracks the *saved*
 * number, not what is currently typed into the field: the checkboxes unlock
 * when the number is actually stored, which is what the field's own status
 * line reports.
 */
const SMS_UNAVAILABLE: ReadonlySet<NotificationChannel> = new Set(["sms"]);

export interface NotificationPreferencesFormSite {
  readonly siteKey: string;
  readonly label: string;
  readonly preferences: NotificationCategoryPreferences;
  /** See `NotificationPreferenceGroupProps.inherited` — typically the "All Cameras" preferences. */
  readonly inherited?: NotificationCategoryPreferences | undefined;
}

export interface CameraNotificationPreference {
  readonly cameraKey: string;
  readonly label: string;
  readonly preferences: NotificationCategoryPreferences;
  /** See `NotificationPreferenceGroupProps.inherited` — typically "All Cameras" merged with this camera's own site. */
  readonly inherited?: NotificationCategoryPreferences | undefined;
}

export interface NotificationPreferencesFormProps {
  readonly allCameras: NotificationCategoryPreferences;
  readonly onToggleAllCamerasChannel: (
    category: NotificationCategory,
    channel: NotificationChannel,
    enabled: boolean,
  ) => void;
  readonly sites: readonly NotificationPreferencesFormSite[];
  readonly onToggleSiteChannel: (
    siteKey: string,
    category: NotificationCategory,
    channel: NotificationChannel,
    enabled: boolean,
  ) => void;
  readonly cameras: readonly CameraNotificationPreference[];
  readonly onToggleCameraChannel: (
    cameraKey: string,
    category: NotificationCategory,
    channel: NotificationChannel,
    enabled: boolean,
  ) => void;
  /** `null` if the user has never set one — see `NotificationContactPhoneField`. */
  readonly phoneNumber: string | null;
  readonly onSavePhoneNumber: (phoneNumber: string | null) => void;
  readonly testID?: string;
}

/**
 * An individual user's own notification settings — self-service, account-holders
 * only (no arbitrary contacts here, unlike the tenant-wide Contacts feature).
 *
 * Three independent scopes, each the same "Email/SMS/Push × Alarm Events/Alarm
 * Setting Changes" shape: a global "All Cameras" default, a "Sites" section
 * listing every site the user has access to, and a "Cameras" section listing
 * every camera — both list sections share `NotificationScopeListGroup`. There's
 * no cascading/precedence between the three modelled here; each is its own set
 * of toggles for the caller to interpret.
 */
export const NotificationPreferencesForm: FC<NotificationPreferencesFormProps> = ({
  allCameras,
  onToggleAllCamerasChannel,
  sites,
  onToggleSiteChannel,
  cameras,
  onToggleCameraChannel,
  phoneNumber,
  onSavePhoneNumber,
  testID,
}) => {
  const unavailableChannels = (phoneNumber ?? "") === "" ? SMS_UNAVAILABLE : NO_UNAVAILABLE_CHANNELS;

  return (
    <View style={styles.container} testID={testID}>
      <NotificationContactPhoneField phoneNumber={phoneNumber} onSave={onSavePhoneNumber} />
      <View style={styles.divider} />
      <NotificationPreferenceGroup
        label="All Cameras"
        preferences={allCameras}
        onToggleChannel={onToggleAllCamerasChannel}
        unavailableChannels={unavailableChannels}
      />
      <View style={styles.divider} />
      <NotificationScopeListGroup
        label="Sites"
        items={sites.map((site) => ({
          key: site.siteKey,
          label: site.label,
          preferences: site.preferences,
          inherited: site.inherited,
        }))}
        onToggleChannel={onToggleSiteChannel}
        unavailableChannels={unavailableChannels}
      />
      <View style={styles.divider} />
      <NotificationScopeListGroup
        label="Cameras"
        items={cameras.map((camera) => ({
          key: camera.cameraKey,
          label: camera.label,
          preferences: camera.preferences,
          inherited: camera.inherited,
        }))}
        onToggleChannel={onToggleCameraChannel}
        unavailableChannels={unavailableChannels}
      />
    </View>
  );
};

const styles = StyleSheet.create((theme) => ({
  container: {
    gap: theme.spacing.lg,
  },
  divider: {
    height: 1,
    backgroundColor: theme.colors.border,
  },
}));
